/**
 * Announcement Configuration Manager
 * 
 * Handles reading and updating the announcement configuration from Supabase.
 * This manager provides utilities to work with announcement data stored in
 * the database, allowing admins to update announcements that all users can see.
 */

import { currentAnnouncement, type Announcement } from '../config/announcements';
import { supabase } from './supabase';

export class AnnouncementConfigManager {
  private static instance: AnnouncementConfigManager;
  private currentConfig: Announcement;
  private listeners: Array<(config: Announcement) => void> = [];
  private dismissedAnnouncements: Set<string> = new Set(); // Track dismissed announcement IDs per user

  constructor() {
    this.currentConfig = { ...currentAnnouncement };
    this.loadDismissedAnnouncements();
    // Load from database on initialization
    this.loadFromDatabase();
  }

  static getInstance(): AnnouncementConfigManager {
    if (!AnnouncementConfigManager.instance) {
      AnnouncementConfigManager.instance = new AnnouncementConfigManager();
    }
    return AnnouncementConfigManager.instance;
  }

  /**
   * Get the current announcement configuration
   */
  getCurrentConfig(): Announcement {
    return { ...this.currentConfig };
  }

  /**
   * Load announcement configuration from database
   */
  async loadFromDatabase(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .eq('is_active', true)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code !== 'PGRST116') { // Not "no rows returned"
          console.warn('⚠️ Failed to load announcements from database:', error);
        }
        return;
      }

      if (data) {
        // Convert database format to our Announcement interface
        const dbAnnouncement: Announcement = {
          id: data.id,
          version: data.version,
          title: data.title,
          items: data.items || [],
          showUntil: data.show_until || undefined,
          isActive: data.is_active,
          updatedAt: data.updated_at
        };

        const previousConfig = { ...this.currentConfig };
        this.currentConfig = dbAnnouncement;

        // If content changed, clear dismissals
        if (this.hasAnnouncementContentChanged(previousConfig, this.currentConfig)) {
          if (this.dismissedAnnouncements.has(this.currentConfig.id)) {
            this.dismissedAnnouncements.delete(this.currentConfig.id);
            this.saveDismissedAnnouncements();
          }
        }

        this.notifyListeners();
        console.log('📢 Loaded announcement from database:', this.currentConfig);
      }
    } catch (error) {
      console.warn('⚠️ Error loading announcements from database:', error);
    }
  }

  /**
   * Update the announcement configuration in database
   */
  async updateConfig(newConfig: Announcement): Promise<{ success: boolean; error?: string }> {
    try {
      // Validate the configuration
      const validation = this.validateConfig(newConfig);
      if (!validation.isValid) {
        return { success: false, error: validation.error };
      }

      const previousConfig = { ...this.currentConfig };

      // Prepare data for database
      const dbData = {
        id: newConfig.id,
        version: newConfig.version,
        title: newConfig.title,
        items: newConfig.items,
        show_until: newConfig.showUntil || null,
        is_active: newConfig.isActive,
        updated_at: new Date().toISOString()
      };

      // Save to database
      const { error } = await supabase
        .from('announcements')
        .upsert(dbData, { onConflict: 'id' });

      if (error) {
        console.error('❌ Failed to save announcement to database:', error);
        return { success: false, error: error.message };
      }

      // Update local config
      this.currentConfig = {
        ...newConfig,
        updatedAt: dbData.updated_at
      };

      // If the announcement content has changed, clear dismissal for this announcement
      if (this.hasAnnouncementContentChanged(previousConfig, this.currentConfig)) {
        if (this.dismissedAnnouncements.has(this.currentConfig.id)) {
          this.dismissedAnnouncements.delete(this.currentConfig.id);
          this.saveDismissedAnnouncements();
          console.log(`📢 Cleared dismissal for updated announcement: ${this.currentConfig.id}`);
        }
      }

      // Notify listeners
      this.notifyListeners();

      console.log('📢 Announcement saved to database:', this.currentConfig);
      return { success: true };

    } catch (error) {
      console.error('❌ Failed to update announcement config:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Check if announcement content has meaningfully changed
   * (title, items, version, or active status)
   */
  private hasAnnouncementContentChanged(oldConfig: Announcement, newConfig: Announcement): boolean {
    // Check if title changed
    if (oldConfig.title !== newConfig.title) {
      return true;
    }

    // Check if version changed
    if (oldConfig.version !== newConfig.version) {
      return true;
    }

    // Check if active status changed
    if (oldConfig.isActive !== newConfig.isActive) {
      return true;
    }

    // Check if items changed (deep comparison)
    if (oldConfig.items.length !== newConfig.items.length) {
      return true;
    }

    // Compare each item
    for (let i = 0; i < oldConfig.items.length; i++) {
      const oldItem = oldConfig.items[i];
      const newItem = newConfig.items[i];
      
      if (oldItem.text !== newItem.text || oldItem.icon !== newItem.icon) {
        return true;
      }
    }

    // Check if showUntil date changed
    if (oldConfig.showUntil !== newConfig.showUntil) {
      return true;
    }

    return false;
  }

  /**
   * Validate announcement configuration
   */
  private validateConfig(config: Announcement): { isValid: boolean; error?: string } {
    if (!config.title?.trim()) {
      return { isValid: false, error: 'Title is required' };
    }

    if (!config.version?.trim()) {
      return { isValid: false, error: 'Version is required' };
    }

    if (!config.id?.trim()) {
      return { isValid: false, error: 'ID is required' };
    }

    // Validate items
    for (const item of config.items) {
      if (!item.text?.trim()) {
        return { isValid: false, error: 'All announcement items must have text' };
      }
      if (!item.id?.trim()) {
        return { isValid: false, error: 'All announcement items must have an ID' };
      }
    }

    // Validate show until date if provided
    if (config.showUntil) {
      const date = new Date(config.showUntil);
      if (isNaN(date.getTime())) {
        return { isValid: false, error: 'Invalid show until date' };
      }
    }

    return { isValid: true };
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: (config: Announcement) => void): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of configuration changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getCurrentConfig());
      } catch (error) {
        console.error('❌ Error in announcement config listener:', error);
      }
    });
  }

  /**
   * Refresh announcement data from database
   */
  async refresh(): Promise<void> {
    await this.loadFromDatabase();
  }

  /**
   * Reset configuration to default
   */
  async resetToDefault(): Promise<{ success: boolean; error?: string }> {
    return this.updateConfig({ ...currentAnnouncement });
  }

  /**
   * Check if announcement should be shown based on current config
   */
  shouldShowAnnouncement(): boolean {
    if (!this.currentConfig.isActive) {
      return false;
    }

    // Check if announcement has expired
    if (this.currentConfig.showUntil) {
      const hideDate = new Date(this.currentConfig.showUntil);
      const now = new Date();
      
      // Compare dates only (same logic as existing utilities)
      const hideDateLocal = new Date(hideDate.getFullYear(), hideDate.getMonth(), hideDate.getDate());
      const nowLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (nowLocal > hideDateLocal) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if the current user has dismissed this announcement
   */
  isAnnouncementDismissed(): boolean {
    return this.dismissedAnnouncements.has(this.currentConfig.id);
  }

  /**
   * Check if red dot should be shown (announcement exists and not dismissed by user)
   */
  shouldShowRedDot(): boolean {
    return this.shouldShowAnnouncement() && !this.isAnnouncementDismissed();
  }

  /**
   * Mark the current announcement as dismissed by this user
   */
  dismissCurrentAnnouncement(): void {
    if (this.currentConfig.id) {
      this.dismissedAnnouncements.add(this.currentConfig.id);
      this.saveDismissedAnnouncements();
      this.notifyListeners();
      console.log(`📢 User dismissed announcement: ${this.currentConfig.id}`);
    }
  }

  /**
   * Load dismissed announcements from localStorage
   */
  private loadDismissedAnnouncements(): void {
    try {
      const stored = localStorage.getItem('nosh_dismissed_announcements');
      if (stored) {
        const dismissedArray = JSON.parse(stored);
        this.dismissedAnnouncements = new Set(dismissedArray);
        console.log('📢 Loaded dismissed announcements:', dismissedArray);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load dismissed announcements from localStorage:', error);
      this.dismissedAnnouncements = new Set();
    }
  }

  /**
   * Save dismissed announcements to localStorage
   */
  private saveDismissedAnnouncements(): void {
    try {
      const dismissedArray = Array.from(this.dismissedAnnouncements);
      localStorage.setItem('nosh_dismissed_announcements', JSON.stringify(dismissedArray));
    } catch (error) {
      console.warn('⚠️ Failed to save dismissed announcements to localStorage:', error);
    }
  }

  /**
   * Get announcement statistics
   */
  getStats(): {
    isActive: boolean;
    itemCount: number;
    hasExpiry: boolean;
    lastUpdated: string;
  } {
    return {
      isActive: this.shouldShowAnnouncement(),
      itemCount: this.currentConfig.items.length,
      hasExpiry: !!this.currentConfig.showUntil,
      lastUpdated: this.currentConfig.updatedAt
    };
  }
}

// Export singleton instance
export const announcementConfigManager = AnnouncementConfigManager.getInstance();

// Note: Database loading happens automatically in constructor